/**
 * Shared helpers for the renameable creation-edge output placeholder.
 *
 * Every wait-state component that exposes a creation-edge port stamps a
 * runtime URL onto its componentData when it enters WAITING. Authors can
 * customise the placeholder key from the drawer; the engine resolver
 * (`CreationPlaceholderConfig` on the backend, `getCreationPlaceholdersForComponent`
 * in placeholderExtractor.js) reads from componentData so the renamed key
 * propagates through the placeholder picker, the runtime stamp, and the
 * downstream Tiptap editors.
 *
 * Default keys auto-generate with a random 8-digit suffix so dragging
 * multiple wait-state components onto the same canvas never silently
 * collides on the same `${taskUrl}` (or `${formUrl}`, etc.). The pattern
 * mirrors `useAIAgentStore`'s chatSessionId default.
 */

const SYSTEM_DEFAULTS_BY_TYPE = {
  dynamic_review_task: { systemOutput: 'taskUrl', type: 'text' },
  ai_agent: { systemOutput: 'agentSessionUrl', type: 'text' },
  form_step: { systemOutput: 'formUrl', type: 'text' },
};

const generateUniqueSuffix = () =>
  Math.floor(Math.random() * 90000000 + 10000000);

/**
 * Mint a fresh `creationOutputPlaceholder` with an auto-uniquified key.
 * Call from a store's `emptyData()` so new components never share the
 * bare default key with their siblings.
 */
export function newCreationOutputPlaceholder(componentType) {
  const cfg = SYSTEM_DEFAULTS_BY_TYPE[componentType];
  if (!cfg) return null;
  return {
    key: `${cfg.systemOutput}_${generateUniqueSuffix()}`,
    systemOutput: cfg.systemOutput,
    type: cfg.type,
  };
}

/**
 * Normalise an existing `creationOutputPlaceholder` value loaded from the
 * backend, generating a fresh unique key when:
 *   - the field is missing (legacy components saved before this feature),
 *   - the key is empty/blank,
 *   - the key is the bare system default (`taskUrl` etc.) — which never
 *     came from the rename UI, only from the legacy hardcoded path.
 *
 * Always returns the same shape so `data.creationOutputPlaceholder.key`
 * is safe to read directly from anywhere in the component drawer.
 */
export function ensureCreationOutputPlaceholder(componentType, existing) {
  const cfg = SYSTEM_DEFAULTS_BY_TYPE[componentType];
  if (!cfg) return null;
  const trimmedKey =
    typeof existing?.key === 'string' ? existing.key.trim() : '';
  const isBareDefault = trimmedKey === cfg.systemOutput;
  if (!trimmedKey || isBareDefault) {
    return {
      key: `${cfg.systemOutput}_${generateUniqueSuffix()}`,
      systemOutput: cfg.systemOutput,
      type: cfg.type,
    };
  }
  return {
    key: trimmedKey,
    systemOutput: cfg.systemOutput,
    type: cfg.type,
  };
}

/**
 * Whether a type exposes a renameable creation-edge placeholder.
 * Single source of truth — UI components key off this rather than
 * hardcoding the type list.
 */
export function isCreationPortAware(componentType) {
  return Object.prototype.hasOwnProperty.call(
    SYSTEM_DEFAULTS_BY_TYPE,
    componentType
  );
}

/**
 * Read the system discriminator (`taskUrl`, `agentSessionUrl`, `formUrl`)
 * for a type. Used by the rename UI to display "system: taskUrl" next to
 * the editable key field.
 */
export function getSystemOutputForType(componentType) {
  return SYSTEM_DEFAULTS_BY_TYPE[componentType]?.systemOutput ?? null;
}
